import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  MenuItem,
  Snackbar,
  Alert,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  CircularProgress,
} from "@mui/material";
import { Delete, Edit } from "@mui/icons-material";
import { db } from "../firebase";
import {
  collection,
  doc,
  getDoc,
  updateDoc,
  arrayUnion,
} from "firebase/firestore";

const AccessControlManager = ({ documentId }) => {
  const [accessControl, setAccessControl] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [newAccess, setNewAccess] = useState({
    email: "",
    role: "",
    status: "active",
  });

  useEffect(() => {
    const fetchAccessControl = async () => {
      try {
        const docRef = doc(collection(db, "clinic"), documentId);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          setAccessControl(docSnap.data().accessControl || []);
        } else {
          setSnackbar({
            open: true,
            message: "Document does not exist",
            severity: "error",
          });
        }
      } catch (error) {
        setSnackbar({
          open: true,
          message: "Error fetching document",
          severity: "error",
        });
      }
    };

    fetchAccessControl();
  }, [documentId]);

  const addOrUpdateAccessControl = async () => {
    if (!newAccess.email || !newAccess.role) {
      setSnackbar({
        open: true,
        message: "Email and role are required!",
        severity: "warning",
      });
      return;
    }

    setLoading(true);
    try {
      const docRef = doc(collection(db, "clinic"), documentId);
      let updatedAccessControl;

      if (isEdit) {
        updatedAccessControl = accessControl.map((entry) =>
          entry.email === selectedEntry.email ? newAccess : entry
        );
      } else {
        updatedAccessControl = [...accessControl, newAccess];
      }

      await updateDoc(docRef, {
        accessControl: updatedAccessControl,
      });

      setAccessControl(updatedAccessControl);
      setSnackbar({
        open: true,
        message: isEdit ? "Access control updated!" : "Access control added!",
        severity: "success",
      });
      setDialogOpen(false);
      setNewAccess({ email: "", role: "", status: "active" });
      setIsEdit(false);
    } catch (error) {
      setSnackbar({
        open: true,
        message: "Error saving access control",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const deleteAccessControl = async (email) => {
    try {
      const filteredAccess = accessControl.filter(
        (entry) => entry.email !== email
      );

      const docRef = doc(collection(db, "clinic"), documentId);
      await updateDoc(docRef, {
        accessControl: filteredAccess,
      });

      setAccessControl(filteredAccess);
      setSnackbar({
        open: true,
        message: "Access control removed successfully!",
        severity: "success",
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: "Error deleting access control",
        severity: "error",
      });
    }
  };

  return (
    <Box p={3} display="flex" flexDirection="column" alignItems="center">
      <Button
        variant="outlined"
        color="secondary"
        onClick={() => {
          setDialogOpen(true);
          setIsEdit(false);
          setNewAccess({ email: "", role: "", status: "active" });
        }}
      >
        Add New Access
      </Button>

      <List>
        {accessControl.map((entry, index) => (
          <ListItem key={index} divider>
            <ListItemText
              primary={`${entry.email} (${entry.role})`}
              secondary={`Status: ${entry.status}`}
            />
            <ListItemSecondaryAction>
              <IconButton
                edge="end"
                aria-label="edit"
                onClick={() => {
                  setSelectedEntry(entry);
                  setNewAccess(entry);
                  setDialogOpen(true);
                  setIsEdit(true);
                }}
              >
                <Edit />
              </IconButton>
              <IconButton
                edge="end"
                aria-label="delete"
                onClick={() => deleteAccessControl(entry.email)}
              >
                <Delete />
              </IconButton>
            </ListItemSecondaryAction>
          </ListItem>
        ))}
      </List>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <DialogTitle>{isEdit ? "Edit Access" : "Add New Access"}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            margin="dense"
            label="Email"
            value={newAccess.email}
            onChange={(e) =>
              setNewAccess({ ...newAccess, email: e.target.value })
            }
            disabled={isEdit}
          />
          <TextField
            fullWidth
            margin="dense"
            select
            label="Role"
            value={newAccess.role}
            onChange={(e) =>
              setNewAccess({ ...newAccess, role: e.target.value })
            }
          >
            <MenuItem value="admin">Admin</MenuItem>
            <MenuItem value="manager">Manager</MenuItem>
          </TextField>
          <TextField
            fullWidth
            margin="dense"
            select
            label="Status"
            value={newAccess.status}
            onChange={(e) =>
              setNewAccess({ ...newAccess, status: e.target.value })
            }
          >
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="inactive">Inactive</MenuItem>
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} color="secondary">
            Cancel
          </Button>
          <Button
            onClick={addOrUpdateAccessControl}
            color="primary"
            variant="contained"
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AccessControlManager;
